/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, memo} from 'react'
import PropTypes from 'prop-types'
import {Stack, Text} from '@chakra-ui/react'
import {useShopperCustomersMutation, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import CustomCheckbox from '../../../../components/custom-checkbox'
import {useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'

const MembershipSubscription = memo(({
    basket,
    customer,
    membershipPreferences,
    setMembershipPreferences
}) => {
    const {mutateAsync: updateBasket, isLoading: basketIsLoading} =
        useShopperBasketsMutation('updateBasket')
    const {mutateAsync: updateCustomer, isLoading: customerIsLoading} =
        useShopperCustomersMutation('updateCustomer')

    const onMembershipPreferencesUpdate = async (type, value) => {
        const newPreferences = {
            ...membershipPreferences,
            [type]: value
        }
        setMembershipPreferences(newPreferences)

        if (customer?.isGuest) {
            await updateBasket({
                parameters: {basketId: basket.basketId},
                body: {c_membershipPreferences: newPreferences}
            })
        } else {
            await updateCustomer({
                parameters: {customerId: customer.customerId},
                body: {
                    c_membershipPreferences: newPreferences
                }
            })
        }
    }

    useEffect(() => {
        if (customer && !customer.isGuest && customer.c_membershipPreferences) {
            setMembershipPreferences(customer.c_membershipPreferences)
        }
    }, [customer?.customerId, customer?.c_membershipPreferences, setMembershipPreferences])

    const {tSubscriptions} = useTranslationHelpers()

    return (
        <Stack>
            <Text fontSize="lg" fontWeight="bold" color="black" pb="5px">
                {tSubscriptions?.becomeMemberTitle}{' '}
                <Text as="span" fontWeight="light" color="black">
                    {tSubscriptions?.becomeMemberNote}
                </Text>
            </Text>
            <Text fontSize="lg" color="gray.300" pb="9px">
                {tSubscriptions?.becomeMemberDescription}
            </Text>
            <Stack direction="row" mt={2} gap={4}>
                <CustomCheckbox
                    name="membershipMen"
                    checked={membershipPreferences.men}
                    onChange={(e) => onMembershipPreferencesUpdate('men', e.target.checked)}
                    disabled={customerIsLoading || basketIsLoading}
                    customStyles={{
                        text: {marginLeft: '17px', color: 'black'},
                        label: {alignItems: 'center'},
                        control: {borderColor: 'gray.300'}
                    }}
                    label={tSubscriptions?.checkboxMen}
                />
                <CustomCheckbox
                    name="membershipWomen"
                    checked={membershipPreferences.women}
                    onChange={(e) => onMembershipPreferencesUpdate('women', e.target.checked)}
                    disabled={customerIsLoading || basketIsLoading}
                    customStyles={{
                        text: {marginLeft: '17px', color: 'black'},
                        label: {alignItems: 'center'},
                        control: {borderColor: 'gray.300'}
                    }}
                    label={tSubscriptions?.checkboxWomen}
                />
            </Stack>
        </Stack>
    )
})

MembershipSubscription.propTypes = {
    basket: PropTypes.object,
    customer: PropTypes.object
}

export default MembershipSubscription
